import { z } from "zod";
import { FlightJoinCode } from "./join.js";
import { ArchetypeIndex, LessonId, StepId, TemplateId } from "./learn.js";
import { StructuredNote } from "./note.js";
import { WineIdentity } from "./wine.js";

/**
 * A saved tasting: the structured note plus the little envelope every
 * method view and the gamification rules need (which wine, blind or not,
 * quality/depth) — the note itself stays untouched inside `note`.
 */
export const TastingDepth = z.enum(["quick", "deep"]);
export type TastingDepth = z.infer<typeof TastingDepth>;

/** What the taster called before the reveal, in a blind tasting. */
export const BlindGuess = z.object({
  grape: z.string().min(1).max(80).optional(),
  country: z.string().length(2).optional(),
});
export type BlindGuess = z.infer<typeof BlindGuess>;

export const SaveTastingRequest = z.object({
  /** An id already upserted via `POST /v1/wines`, or the identity to upsert
   * in the same call — exactly one of the two, enforced below. Absent for a
   * still-blind solo tasting (see `PATCH /v1/tastings/:id/reveal`). */
  wineId: z.string().min(1).optional(),
  wine: WineIdentity.optional(),
  note: StructuredNote,
  depth: TastingDepth,
  blind: z.boolean().default(false),
  blindGuess: BlindGuess.optional(),
  tastedAt: z.string().datetime().optional(),
});
export type SaveTastingRequest = z.infer<typeof SaveTastingRequest>;

/** Where a flight note came from, so the note picker and the auto-attach map it to a lesson slot without a second request. */
export const TastingFlightRef = z.object({
  code: FlightJoinCode,
  flightWineId: z.string().min(1).max(64),
  archetypeIndex: ArchetypeIndex.exactOptional(),
  lessonId: LessonId.exactOptional(),
  stepId: StepId.exactOptional(),
  templateId: TemplateId.exactOptional(),
});
export type TastingFlightRef = z.infer<typeof TastingFlightRef>;

export const Tasting = z.object({
  id: z.string().min(1),
  wine: WineIdentity.nullable(),
  note: StructuredNote,
  depth: TastingDepth,
  blind: z.boolean(),
  blindGuess: BlindGuess.optional(),
  blindCorrect: z.boolean().nullable(),
  tastedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  /** Present only on the flight notes `GET /v1/tastings?include=flight` appends. */
  flight: TastingFlightRef.exactOptional(),
});
export type Tasting = z.infer<typeof Tasting>;

/** `PATCH /v1/tastings/:id/reveal` body — resolves a blind solo tasting once
 * the taster looks at the bottle, scoring the guess against the real wine. */
export const RevealTastingRequest = z.object({
  wineId: z.string().min(1).optional(),
  wine: WineIdentity.optional(),
});
export type RevealTastingRequest = z.infer<typeof RevealTastingRequest>;

export const ListTastingsResponse = z.object({
  items: z.array(Tasting),
  total: z.number().int().nonnegative(),
});
export type ListTastingsResponse = z.infer<typeof ListTastingsResponse>;

/** `GET /v1/gamification/summary` — every collection's progress and every
 * achievement's satisfied state, recomputed fresh from the caller's own
 * tastings each time (packages/domain's rules are pure predicates; nothing
 * about "unlocked" needs its own storage). */
export const CollectionProgressView = z.object({
  collectionId: z.string(),
  kind: z.enum(["grape", "region", "country", "style", "producer"]),
  title: z.string(),
  done: z.number().int().nonnegative(),
  total: z.number().int().positive(),
  missing: z.array(z.string()),
  complete: z.boolean(),
});
export type CollectionProgressView = z.infer<typeof CollectionProgressView>;

export const AchievementView = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  satisfied: z.boolean(),
});
export type AchievementView = z.infer<typeof AchievementView>;

export const GamificationSummary = z.object({
  collections: z.array(CollectionProgressView),
  achievements: z.array(AchievementView),
  weeklyStreak: z.number().int().nonnegative(),
  longestBlindStreak: z.number().int().nonnegative(),
  totalTastings: z.number().int().nonnegative(),
});
export type GamificationSummary = z.infer<typeof GamificationSummary>;
